import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send, Loader2, Facebook, Instagram, Youtube, Twitter } from 'lucide-react';
import PageHero from '@/components/PageHero';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { SCHOOL } from '@/data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full rounded-lg border border-navy/15 bg-white px-4 py-3 text-sm text-navy placeholder:text-navy/40 focus:border-crimson focus:outline-none focus:ring-2 focus:ring-crimson/20 transition';

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '', website: '' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, phone: form.phone || undefined };
      const res = await axios.post(`${API}/contact`, payload);
      toast.success(res.data.message || 'Message sent successfully!');
      setForm({ name: '', email: '', phone: '', subject: '', message: '', website: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || 'Could not send. Please call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="rounded-xl border border-navy/10 bg-white p-6 sm:p-10 shadow-sm">
      <h3 className="font-display text-2xl font-bold text-navy">Send us a message</h3>
      <p className="mt-2 text-sm text-navy/60">We usually reply within one working day.</p>
      <input type="text" name="website" value={form.website} onChange={set('website')} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Your Name *</label>
          <input id="contact-name" data-testid="contact-name" required minLength={2} value={form.name} onChange={set('name')} className={inputCls} placeholder="Full name" />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Email *</label>
          <input id="contact-email" data-testid="contact-email" type="email" required value={form.email} onChange={set('email')} className={inputCls} placeholder="you@example.com" />
        </div>
        <div>
          <label htmlFor="contact-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Phone (optional)</label>
          <input id="contact-phone" data-testid="contact-phone" maxLength={15} value={form.phone} onChange={set('phone')} className={inputCls} placeholder="10-digit mobile" />
        </div>
        <div>
          <label htmlFor="contact-subject" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Subject *</label>
          <input id="contact-subject" data-testid="contact-subject" required minLength={2} value={form.subject} onChange={set('subject')} className={inputCls} placeholder="Admission, transport, feedback..." />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="contact-message" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-navy/60">Message *</label>
          <textarea id="contact-message" data-testid="contact-message" required minLength={5} rows={4} value={form.message} onChange={set('message')} className={inputCls} placeholder="How can we help you?" />
        </div>
      </div>
      <button type="submit" data-testid="contact-submit" disabled={loading} className="mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-crimson px-8 py-3.5 text-sm font-bold text-white transition-all hover:bg-crimson-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
};

const Contact = () => (
  <>
    <PageHero eyebrow="Contact Us" title="We would love to hear from you." description="Visit us, call us, or drop a message — the school office is open Monday to Saturday." />

    <section className="py-24 sm:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5 space-y-6">
          <SectionHeading number="01" eyebrow="Get in Touch" title="Reach the school office" />
          {[
            { Icon: MapPin, title: 'Address', lines: [SCHOOL.address], testid: 'contact-address' },
            { Icon: Phone, title: 'Phone', lines: SCHOOL.phones, links: SCHOOL.phones.map((p) => `tel:${p.replace(/\s/g, '')}`), testid: 'contact-phones' },
            { Icon: Mail, title: 'Email', lines: [SCHOOL.email], links: [`mailto:${SCHOOL.email}`], testid: 'contact-email-card' },
            { Icon: Clock, title: 'Office Timings', lines: [`Office: ${SCHOOL.officeHours}`, `Classes: ${SCHOOL.hours}`], testid: 'contact-hours' },
          ].map(({ Icon, title, lines, links, testid }, i) => (
            <Reveal key={title} delay={i * 0.07}>
              <div data-testid={testid} className="flex gap-4 rounded-lg border border-navy/10 bg-white p-6 transition-all hover:-translate-y-1 hover:border-gold">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-gold shrink-0"><Icon className="h-5 w-5" /></span>
                <div>
                  <h3 className="font-bold text-navy">{title}</h3>
                  {lines.map((l, j) =>
                    links ? (
                      <a key={l} href={links[j]} data-testid={`${testid}-link-${j}`} className="block text-sm text-navy/60 hover:text-crimson transition-colors mt-1">{l}</a>
                    ) : (
                      <p key={l} className="text-sm text-navy/60 mt-1">{l}</p>
                    )
                  )}
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy/50">Follow us</span>
              {[
                { Icon: Facebook, label: 'Facebook' },
                { Icon: Instagram, label: 'Instagram' },
                { Icon: Youtube, label: 'YouTube' },
                { Icon: Twitter, label: 'Twitter' },
              ].map(({ Icon, label }) => (
                <a key={label} href="#" aria-label={label} data-testid={`contact-social-${label.toLowerCase()}`} className="flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 text-navy transition-all hover:border-crimson hover:text-crimson hover:-translate-y-0.5">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.15}><ContactForm /></Reveal>
        </div>
      </div>
    </section>

    <section className="pb-24 sm:pb-32" data-testid="map-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="02" eyebrow="Find Us" title="On the map" />
        <Reveal delay={0.15}>
          <div className="mt-10 overflow-hidden rounded-xl border border-navy/10">
            <iframe
              data-testid="google-map"
              title="Ideal Public School location map"
              src={`https://www.google.com/maps?q=${encodeURIComponent(SCHOOL.mapQuery)}&output=embed`}
              className="h-[420px] w-full"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Contact;
